import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Users, 
  MessageCircle, 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Send, 
  Paperclip, 
  Calendar,
  Clock,
  CheckCircle,
  X,
  Eye,
  Star,
  AlertCircle,
  UserPlus,
  Settings,
  Video,
  FileQuestion,
  MessageSquare
} from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  teacherHiringService, 
  batchContentService, 
  tuitionTeacherService 
} from '../firebase/tuition';
import { useRealtime } from '../contexts/RealtimeContext';
import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  getDocs, 
  doc, 
  updateDoc 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  getBatchSyllabus, 
  saveBatchSyllabus,
  updateSyllabusProgress 
} from '../firebase/collections';
import { 
  ChevronDown, 
  ChevronRight, 
  CheckCircle2, 
  Circle,
  BookOpen as SyllabusIcon
} from 'lucide-react';

const TeacherBatchManagement = () => {
  const { currentUser: user } = useRealtime();
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchContent, setBatchContent] = useState({
    posts: [],
    assignments: [],
    discussions: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('post'); // post, assignment, discussion
  const [batchMembers, setBatchMembers] = useState([]);
  const [assignmentStats, setAssignmentStats] = useState({});
  const [showCreateBatchModal, setShowCreateBatchModal] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchDescription, setNewBatchDescription] = useState('');
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestType, setRequestType] = useState('test'); // 'test' or 'video'
  const [requestMessage, setRequestMessage] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [teacherRequests, setTeacherRequests] = useState([]);
  const [showRequestsSection, setShowRequestsSection] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestDetailsModal, setShowRequestDetailsModal] = useState(false);
  const [teacherReplyMessage, setTeacherReplyMessage] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [syllabus, setSyllabus] = useState(null);
  const [loadingSyllabus, setLoadingSyllabus] = useState(false);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [expandedTopics, setExpandedTopics] = useState({});

  useEffect(() => {
    if (user) {
      loadTeacherBatches();
    }
  }, [user]);

  useEffect(() => {
    if (selectedBatch) {
      loadBatchContent();
      loadBatchMembers();
      loadSyllabus();
    }
  }, [selectedBatch]);

  const loadSyllabus = async () => {
    if (!selectedBatch) return;
    setLoadingSyllabus(true);
    try {
      const result = await getBatchSyllabus(selectedBatch.id);
      if (result.success && result.data) {
        setSyllabus(result.data);
      }
    } catch (error) {
      console.error('Error loading syllabus:', error);
    } finally {
      setLoadingSyllabus(false);
    }
  };

  const handleToggleSubtopic = async (subjectIndex, topicIndex, subtopicIndex) => {
    if (!selectedBatch || !syllabus) return;

    const newSyllabus = { ...syllabus };
    const subtopic = newSyllabus.subjects[subjectIndex].topics[topicIndex].subtopics[subtopicIndex];
    
    subtopic.isDone = !subtopic.isDone;
    subtopic.doneAt = subtopic.isDone ? new Date().toISOString() : null;

    // Auto-update topic status
    const topic = newSyllabus.subjects[subjectIndex].topics[topicIndex];
    const allSubtopicsDone = topic.subtopics.length > 0 && topic.subtopics.every(s => s.isDone);
    
    topic.isDone = allSubtopicsDone;
    if (allSubtopicsDone) {
      // Find the latest doneAt from subtopics
      const latestDoneAt = topic.subtopics.reduce((latest, s) => {
        if (!latest || (s.doneAt && new Date(s.doneAt) > new Date(latest))) {
          return s.doneAt;
        }
        return latest;
      }, null);
      topic.doneAt = latestDoneAt;
    } else {
      topic.doneAt = null;
    }

    setSyllabus(newSyllabus);
    
    try {
      await saveBatchSyllabus(selectedBatch.id, newSyllabus.subjects);
      toast.success('Progress updated');
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const calculateSubjectProgress = (subject) => {
    let total = 0;
    let completed = 0;
    subject.topics.forEach(topic => {
      if (topic.subtopics.length === 0) {
        total += 1;
        if (topic.isDone) completed += 1;
      } else {
        total += topic.subtopics.length;
        completed += topic.subtopics.filter(s => s.isDone).length;
      }
    });
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  };

  useEffect(() => {
    if (selectedBatch && batchContent.assignments.length > 0) {
      loadAssignmentStats();
    }
  }, [selectedBatch, batchContent.assignments]);

  useEffect(() => {
    if (user) {
      const unsubscribe = loadTeacherRequests();
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [user]);

  const loadTeacherBatches = async () => {
    try {
      setLoading(true);
      const teacherBatches = await teacherHiringService.getTeacherBatches(user.uid);
      setBatches(teacherBatches);
      if (teacherBatches.length > 0 && !selectedBatch) {
        setSelectedBatch(teacherBatches[0]);
      }
    } catch (error) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchContent = async () => {
    if (!selectedBatch) return;
    
    try {
      const [posts, assignments, discussions] = await Promise.all([
        batchContentService.getBatchPosts(selectedBatch.id),
        batchContentService.getBatchAssignments(selectedBatch.id),
        batchContentService.getBatchDiscussions(selectedBatch.id)
      ]);
      
      setBatchContent({ posts, assignments, discussions });
    } catch (error) {
      toast.error('Failed to load batch content');
    }
  };

  const loadBatchMembers = async () => {
    if (!selectedBatch) return;
    
    try {
      const members = await teacherHiringService.getBatchMembers(selectedBatch.id);
      setBatchMembers(members);
    } catch (error) {
      toast.error('Failed to load batch members');
    }
  };

  const handleCreateContent = async (contentData) => {
    if (!selectedBatch) return;
    
    try {
      const content = {
        ...contentData,
        authorId: user.uid,
        authorName: user.displayName || user.email,
        authorRole: 'teacher',
        attachments: contentData.attachments || []
      };

      let result;
      switch (createType) {
        case 'post':
          result = await batchContentService.createBatchPost(selectedBatch.id, content);
          break;
        case 'assignment':
          result = await batchContentService.createBatchAssignment(selectedBatch.id, content);
          break;
        case 'discussion':
          result = await batchContentService.createBatchDiscussion(selectedBatch.id, content);
          break;
        default:
          throw new Error('Invalid content type');
      }

      toast.success(`${createType.charAt(0).toUpperCase() + createType.slice(1)} created successfully!`);
      setShowCreateModal(false);
      loadBatchContent();
    } catch (error) {
      toast.error(`Failed to create ${createType}`);
    }
  };

  const loadAssignmentStats = async () => {
    if (!selectedBatch) return;
    
    try {
      const stats = {};
      for (const assignment of batchContent.assignments) {
        const assignmentStats = await batchContentService.getAssignmentStats(assignment.id, selectedBatch.id);
        stats[assignment.id] = assignmentStats;
      }
      setAssignmentStats(stats);
    } catch (error) {
      // Failed to load assignment stats
    }
  };

  const handleDeleteFile = async (postId, fileIndex, contentType = 'post') => {
    if (!selectedBatch) return;
    
    try {
      const content = contentType === 'post' 
        ? batchContent.posts.find(p => p.id === postId)
        : batchContent.assignments.find(a => a.id === postId);
      
      if (!content || !content.attachments || !content.attachments[fileIndex]) {
        toast.error('File not found');
        return;
      }

      const file = content.attachments[fileIndex];
      
      // Delete from storage
      await batchContentService.deleteBatchFile(file.path);
      
      // Update content to remove file
      const updatedAttachments = content.attachments.filter((_, index) => index !== fileIndex);
      
      if (contentType === 'post') {
        await batchContentService.updatePostFiles(postId, updatedAttachments);
      } else {
        await batchContentService.updateAssignmentFiles(postId, updatedAttachments);
      }
      
      toast.success('File deleted successfully');
      loadBatchContent();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleDeleteContent = async (type, contentId) => {
    if (!selectedBatch) return;
    
    try {
      switch (type) {
        case 'post':
          await batchContentService.deleteBatchPost(contentId);
          break;
        case 'assignment':
          await batchContentService.deleteBatchAssignment(contentId);
          break;
        case 'discussion':
          await batchContentService.deleteBatchDiscussion(contentId);
          break;
        default:
          throw new Error('Invalid content type');
      }
      
      toast.success(`${type} deleted successfully!`);
      loadBatchContent();
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await teacherHiringService.removeStudentFromBatch(memberId);
      loadBatchMembers();
    } catch (error) {
      throw error;
    }
  };

  const handleCreateBatch = async () => {
    if (!newBatchName.trim()) {
      toast.error('Please enter a batch name');
      return;
    }

    if (!user) {
      toast.error('User not found');
      return;
    }

    setIsCreatingBatch(true);
    try {
      const batchId = await teacherHiringService.createTeacherBatch(user.uid, {
        batchName: newBatchName.trim(),
        description: newBatchDescription.trim(),
        studentCount: 0
      });
      
      toast.success('Batch created successfully!');
      setNewBatchName('');
      setNewBatchDescription('');
      setShowCreateBatchModal(false);
      
      // Reload batches after a short delay to ensure data is synced
      setTimeout(() => {
        loadTeacherBatches();
      }, 500);
    } catch (error) {
      toast.error(error.message || 'Failed to create batch. Please try again.');
    } finally {
      setIsCreatingBatch(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch? This will permanently delete all batch content, assignments, posts, discussions, and student data. This action cannot be undone.')) {
      return;
    }

    if (!user) {
      toast.error('User not found');
      return;
    }

    try {
      await teacherHiringService.deleteTeacherBatch(batchId, user.uid);
      toast.success('Batch deleted successfully!');
      
      // Clear selected batch if it was the deleted one
      if (selectedBatch?.id === batchId) {
        setSelectedBatch(null);
        setBatchContent({ posts: [], assignments: [], discussions: [] });
        setBatchMembers([]);
      }
      
      // Reload batches
      loadTeacherBatches();
    } catch (error) {
      toast.error(error.message || 'Failed to delete batch. Please try again.');
    }
  };

  const loadTeacherRequests = () => {
    if (!user) return;

    const q = query(
      collection(db, 'teacherRequests'),
      where('teacherId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Only show pending and in_progress requests (hide completed and rejected)
        if (data.status === 'pending' || data.status === 'in_progress' || data.status === 'approved') {
          requests.push({ id: doc.id, ...data });
        }
      });
      
      // Sort by creation date in memory (descending)
      requests.sort((a, b) => {
        const aTime = a.createdAt?.seconds || (a.createdAt?.getTime ? a.createdAt.getTime() / 1000 : 0);
        const bTime = b.createdAt?.seconds || (b.createdAt?.getTime ? b.createdAt.getTime() / 1000 : 0);
        return bTime - aTime;
      });
      
      setTeacherRequests(requests);
      
      // Update selectedRequest if it exists and matches an updated request
      if (selectedRequest) {
        const updatedRequest = requests.find(r => r.id === selectedRequest.id);
        if (updatedRequest) {
          setSelectedRequest(updatedRequest);
        }
      }
    }, (error) => {
      // Error loading requests
    });

    return unsubscribe;
  };

  const handleSubmitRequest = async () => {
    if (!selectedBatch) {
      toast.error('Please select a batch first');
      return;
    }

    if (!user) {
      toast.error('User not found');
      return;
    }

    if (!requestMessage.trim()) {
      toast.error('Please provide details about your request');
      return;
    }

    setIsSubmittingRequest(true);
    try {
      const requestData = {
        teacherId: user.uid,
        teacherName: user.displayName || user.email || 'Unknown Teacher',
        batchId: selectedBatch.id,
        batchName: selectedBatch.batchName,
        requestType: requestType, // 'test' or 'video'
        message: requestMessage.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'teacherRequests'), requestData);
      
      toast.success('Request submitted successfully! Admin will review it soon.');
      setRequestMessage('');
      setRequestType('test');
      setShowRequestModal(false);
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="h-full bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-gray-600 dark:text-gray-400">Loading batches...</p>
        </div>
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="h-full bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <BookOpen size={48} className="sm:w-16 sm:h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">No batches yet</h3>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4">
            Create your first batch to start organizing your students and content.
          </p>
          <button
            onClick={() => setShowCreateBatchModal(true)}
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            Create Your First Batch
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 dark:bg-gray-900 px-2 sm:px-3 lg:px-4 pt-2 pb-2 sm:pb-3 lg:pb-4 flex flex-col">
      <div className="h-full w-full flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4 flex-1 min-h-0">
          {/* Batch List Sidebar */}
          <div className="lg:col-span-1 flex flex-col h-full space-y-2 sm:space-y-3">
            {/* Batches Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col flex-1 min-h-0">
              <div className="p-2 sm:p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    Your Batches ({batches.length}/3)
                  </h2>
                  {batches.length < 3 && (
                    <button
                      onClick={() => setShowCreateBatchModal(true)}
                      className="p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Create new batch"
                    >
                      <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="p-2 sm:p-3 space-y-2 overflow-y-auto flex-1 min-h-0">
                {batches.map((batch) => (
                  <motion.div
                    key={batch.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-2 sm:p-3 rounded-lg transition-colors ${
                      selectedBatch?.id === batch.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                        : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div 
                        onClick={() => setSelectedBatch(batch)}
                        className="flex-1 cursor-pointer"
                      >
                        <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-1 truncate">
                          {batch.batchName}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5 line-clamp-2">
                          {batch.description || 'No description'}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>{batch.studentCount || 0} students</span>
                          <span className="truncate ml-2">{formatDate(batch.createdAt)}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBatch(batch.id);
                        }}
                        className="ml-2 p-1.5 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete batch"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Admin Requests Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col flex-1 min-h-0">
              <div className="p-2 sm:p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    Admin Requests ({teacherRequests.length})
                  </h2>
                  <button
                    onClick={() => setShowRequestsSection(!showRequestsSection)}
                    className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    title={showRequestsSection ? 'Collapse' : 'Expand'}
                  >
                    {showRequestsSection ? (
                      <X className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              {showRequestsSection && (
                <div className="p-2 sm:p-3 space-y-2 overflow-y-auto flex-1 min-h-0">
                  {teacherRequests.length === 0 ? (
                    <div className="text-center py-4">
                      <MessageSquare className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500 dark:text-gray-400">No active requests</p>
                    </div>
                  ) : (
                    teacherRequests.map((request) => (
                      <motion.div
                        key={request.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => setSelectedRequest(selectedRequest?.id === request.id ? null : request)}
                        className={`p-2 rounded-lg cursor-pointer transition-colors border ${
                          selectedRequest?.id === request.id
                            ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
                            : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center space-x-1.5">
                            {request.requestType === 'test' ? (
                              <FileQuestion className="w-3 h-3 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                            ) : (
                              <Video className="w-3 h-3 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                            )}
                            <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                              {request.requestType === 'test' ? 'Test' : 'Video'}
                            </span>
                          </div>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                            request.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                              : request.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                          }`}>
                            {request.status === 'pending' ? 'Pending' : request.status === 'in_progress' ? 'In Progress' : 'Approved'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 line-clamp-2">
                          {request.batchName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {request.createdAt?.seconds ? new Date(request.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </p>
                        {selectedRequest?.id === request.id && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRequest(request);
                                setShowRequestDetailsModal(true);
                              }}
                              className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                            >
                              View Details
                            </button>
                          </div>
                        )}
                      </motion.div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-4 flex flex-col h-full">
            {selectedBatch ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col h-full">
                {/* Batch Header - Compact */}
                <div className="p-2 sm:p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white truncate">
                        {selectedBatch.batchName}
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate mt-0.5">
                        {selectedBatch.description || 'No description provided'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-3">
                      <button
                        onClick={() => setShowRequestModal(true)}
                        className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs sm:text-sm whitespace-nowrap"
                      >
                        <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Request Admin</span>
                        <span className="sm:hidden">Request</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Batch Stats - Compact */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="text-center">
                      <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {batchMembers.length}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Students</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600 dark:text-green-400">
                        {batchContent.posts.length + batchContent.assignments.length + batchContent.discussions.length}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total Content</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {batchContent.assignments.length}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Assignments</div>
                    </div>
                  </div>
                </div>

                {/* Tab Navigation - Compact */}
                <div className="border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <nav className="flex space-x-2 sm:space-x-4 lg:space-x-6 px-2 sm:px-4 overflow-x-auto">
                    {[
                      { id: 'overview', label: 'Overview', icon: Eye },
                      { id: 'posts', label: 'Notes', icon: FileText },
                      { id: 'assignments', label: 'Assignments', icon: BookOpen },
                      { id: 'syllabus', label: 'Syllabus', icon: SyllabusIcon },
                      { id: 'discussions', label: 'Discussions', icon: MessageCircle },
                      { id: 'members', label: 'Members', icon: Users }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-2 sm:py-3 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        <tab.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content - Full Height */}
                <div className="p-2 sm:p-3 lg:p-4 flex-1 overflow-y-auto min-h-0">
                  {activeTab === 'overview' && (
                    <OverviewTab 
                      batch={selectedBatch}
                      content={batchContent}
                      members={batchMembers}
                    />
                  )}
                  
                  {activeTab === 'posts' && (
                    <PostsTab 
                      posts={batchContent.posts}
                      onCreatePost={() => {
                        setCreateType('post');
                        setShowCreateModal(true);
                      }}
                      onDeletePost={(postId) => handleDeleteContent('post', postId)}
                      onDeleteFile={handleDeleteFile}
                    />
                  )}
                  
                  {activeTab === 'assignments' && (
                    <AssignmentsTab 
                      assignments={batchContent.assignments}
                      assignmentStats={assignmentStats}
                      onCreateAssignment={() => {
                        setCreateType('assignment');
                        setShowCreateModal(true);
                      }}
                      onDeleteAssignment={(assignmentId) => handleDeleteContent('assignment', assignmentId)}
                      onDeleteFile={handleDeleteFile}
                      batchId={selectedBatch.id}
                    />
                  )}
                  
                  {activeTab === 'syllabus' && (
                    <div className="space-y-4">
                      {loadingSyllabus ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : !syllabus || !syllabus.subjects || syllabus.subjects.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                          <SyllabusIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No syllabus available for this batch yet.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {syllabus.subjects.map((subject, sIdx) => {
                            const progress = calculateSubjectProgress(subject);
                            const isExpanded = expandedSubjects[sIdx];

                            return (
                              <div key={sIdx} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                                <div 
                                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                  onClick={() => setExpandedSubjects(prev => ({ ...prev, [sIdx]: !prev[sIdx] }))}
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-3">
                                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                        <SyllabusIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                      </div>
                                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{subject.name}</h3>
                                    </div>
                                    <div className="mt-3 flex items-center space-x-4">
                                      <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <div 
                                          className="h-full bg-blue-600 dark:bg-blue-500 transition-all duration-500"
                                          style={{ width: `${progress}%` }}
                                        />
                                      </div>
                                      <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{progress}%</span>
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                                  </div>
                                </div>

                                {isExpanded && (
                                  <div className="border-t border-gray-100 dark:border-gray-700 p-4 bg-gray-50/30 dark:bg-gray-800/30">
                                    <div className="space-y-3">
                                      {subject.topics.map((topic, tIdx) => {
                                        const topicKey = `${sIdx}-${tIdx}`;
                                        const isTopicExpanded = expandedTopics[topicKey];

                                        return (
                                          <div key={tIdx} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
                                            <div 
                                              className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                              onClick={() => setExpandedTopics(prev => ({ ...prev, [topicKey]: !prev[topicKey] }))}
                                            >
                                              <div className="flex items-center space-x-3">
                                                {topic.isDone ? (
                                                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                                                ) : (
                                                  <Circle className="w-5 h-5 text-gray-300" />
                                                )}
                                                <div className="flex flex-col">
                                                  {topic.isDone && topic.doneAt && (
                                                    <span className="text-[10px] text-green-600 dark:text-green-400 font-medium mb-0.5">
                                                      Done: {new Date(topic.doneAt).toLocaleDateString('en-IN', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                      })}
                                                    </span>
                                                  )}
                                                  <span className={`font-semibold ${topic.isDone ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                                                    {topic.name}
                                                  </span>
                                                </div>
                                              </div>
                                              {topic.subtopics.length > 0 && (
                                                <div>
                                                  {isTopicExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                                </div>
                                              )}
                                            </div>

                                            {isTopicExpanded && topic.subtopics.length > 0 && (
                                              <div className="bg-gray-50/50 dark:bg-gray-900/20 px-4 py-2 space-y-2 border-t border-gray-50 dark:border-gray-700">
                                                {topic.subtopics.map((subtopic, stIdx) => (
                                                  <div 
                                                    key={stIdx} 
                                                    className="flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-white dark:hover:bg-gray-800 group"
                                                    onClick={() => handleToggleSubtopic(sIdx, tIdx, stIdx)}
                                                  >
                                                    <div className="flex items-center space-x-3">
                                                      {subtopic.isDone ? (
                                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                                      ) : (
                                                        <Circle className="w-4 h-4 text-gray-300" />
                                                      )}
                                                      <span className={`text-sm ${subtopic.isDone ? 'text-gray-500 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                                                        {subtopic.name}
                                                      </span>
                                                    </div>
                                                    <span className="text-[10px] font-medium text-blue-500 opacity-0 group-hover:opacity-100">Click to toggle</span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'discussions' && (
                    <DiscussionsTab 
                      discussions={batchContent.discussions}
                      onCreateDiscussion={() => {
                        setCreateType('discussion');
                        setShowCreateModal(true);
                      }}
                      onDeleteDiscussion={(discussionId) => handleDeleteContent('discussion', discussionId)}
                    />
                  )}
                  
                  {activeTab === 'members' && (
                    <MembersTab 
                      members={batchMembers}
                      batchId={selectedBatch.id}
                      onRemoveMember={handleRemoveMember}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
                <BookOpen size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Select a batch to manage
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Choose a batch from the sidebar to view and manage its content.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Create Content Modal */}
        {showCreateModal && (
          <CreateContentModal
            type={createType}
            batchId={selectedBatch?.id}
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateContent}
          />
        )}

        {/* Create Batch Modal */}
        {showCreateBatchModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Create New Batch
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateBatchModal(false);
                      setNewBatchName('');
                      setNewBatchDescription('');
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Batch Name *
                    </label>
                    <input
                      type="text"
                      value={newBatchName}
                      onChange={(e) => setNewBatchName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter batch name"
                      maxLength={50}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      value={newBatchDescription}
                      onChange={(e) => setNewBatchDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter batch description"
                      rows={3}
                      maxLength={200}
                    />
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                    <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-300">
                      <strong>Note:</strong> You can create up to 3 batches. Each batch can contain multiple students.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowCreateBatchModal(false);
                      setNewBatchName('');
                      setNewBatchDescription('');
                    }}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateBatch}
                    disabled={isCreatingBatch || !newBatchName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isCreatingBatch ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Create Batch</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Request Admin Modal */}
        {showRequestModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full"
            >
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                    Request Admin Support
                  </h3>
                  <button
                    onClick={() => {
                      setShowRequestModal(false);
                      setRequestMessage('');
                      setRequestType('test');
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Request Type
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setRequestType('test')}
                        className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center space-y-2 ${
                          requestType === 'test'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <FileQuestion className={`w-6 h-6 ${requestType === 'test' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${requestType === 'test' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          Create Test
                        </span>
                      </button>
                      <button
                        onClick={() => setRequestType('video')}
                        className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center space-y-2 ${
                          requestType === 'video'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <Video className={`w-6 h-6 ${requestType === 'video' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${requestType === 'video' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          Upload Video
                        </span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Batch
                    </label>
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedBatch?.batchName || 'No batch selected'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {requestType === 'test' ? 'Test Details' : 'Video Details'} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={requestMessage}
                      onChange={(e) => setRequestMessage(e.target.value)}
                      placeholder={
                        requestType === 'test'
                          ? 'Please provide details about the test you want to create (subject, topics, number of questions, duration, etc.)'
                          : 'Please provide details about the video lecture you want to upload (topic, subject, description, etc.)'
                      }
                      rows={5}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>

                  <div className="flex items-center space-x-3 pt-2">
                    <button
                      onClick={() => {
                        setShowRequestModal(false);
                        setRequestMessage('');
                        setRequestType('test');
                      }}
                      className="flex-1 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitRequest}
                      disabled={isSubmittingRequest || !requestMessage.trim()}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {isSubmittingRequest ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Submit Request</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Request Details Modal */}
        {showRequestDetailsModal && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {selectedRequest.requestType === 'test' ? (
                      <FileQuestion className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    ) : (
                      <Video className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    )}
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                        Request Details
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedRequest.requestType === 'test' ? 'Test Creation' : 'Video Upload'} Request
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowRequestDetailsModal(false);
                      setSelectedRequest(null);
                      setTeacherReplyMessage('');
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                <div className="space-y-4">
                  {/* Request Info */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Batch</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedRequest.batchName}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</p>
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          selectedRequest.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                            : selectedRequest.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                            : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                        }`}>
                          {selectedRequest.status === 'pending' ? 'Pending' : selectedRequest.status === 'in_progress' ? 'In Progress' : 'Approved'}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Created</p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedRequest.createdAt?.seconds ? new Date(selectedRequest.createdAt.seconds * 1000).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      {selectedRequest.updatedAt && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Last Updated</p>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedRequest.updatedAt?.seconds ? new Date(selectedRequest.updatedAt.seconds * 1000).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Request Message */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Request:</p>
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selectedRequest.message}</p>
                    </div>
                  </div>

                  {/* Admin Response */}
                  {selectedRequest.adminResponse && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Admin Response:</p>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selectedRequest.adminResponse}</p>
                      </div>
                    </div>
                  )}

                  {/* Discussions/Comments */}
                  {selectedRequest.discussions && selectedRequest.discussions.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Discussions:</p>
                      <div className="space-y-3">
                        {selectedRequest.discussions.map((discussion, index) => {
                          const isTeacher = discussion.authorId === user?.uid || discussion.authorName === (user?.displayName || user?.email);
                          return (
                            <div key={index} className={`rounded-lg p-3 border ${
                              isTeacher
                                ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
                                : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                            }`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <p className={`text-xs font-medium ${
                                    isTeacher
                                      ? 'text-purple-900 dark:text-purple-300'
                                      : 'text-gray-900 dark:text-white'
                                  }`}>
                                    {discussion.authorName || (isTeacher ? 'You' : 'Admin')}
                                  </p>
                                  {isTeacher && (
                                    <span className="text-xs px-1.5 py-0.5 bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 rounded">
                                      You
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {discussion.createdAt?.seconds 
                                    ? new Date(discussion.createdAt.seconds * 1000).toLocaleString() 
                                    : discussion.createdAt instanceof Date
                                    ? discussion.createdAt.toLocaleString()
                                    : 'N/A'}
                                </p>
                              </div>
                              <p className={`text-sm whitespace-pre-wrap ${
                                isTeacher
                                  ? 'text-purple-900 dark:text-purple-200'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}>{discussion.message}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {!selectedRequest.adminResponse && selectedRequest.status === 'pending' && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                      <p className="text-xs text-yellow-800 dark:text-yellow-300">
                        Your request is being reviewed by the admin. You will be notified once there's an update.
                      </p>
                    </div>
                  )}

                  {/* Teacher Reply Section - Show if admin has responded or there are discussions */}
                  {(selectedRequest.adminResponse || selectedRequest.discussions?.length > 0 || selectedRequest.status !== 'pending') && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reply to Admin:</p>
                      <textarea
                        value={teacherReplyMessage}
                        onChange={(e) => setTeacherReplyMessage(e.target.value)}
                        placeholder="Type your reply here..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm mb-2"
                      />
                      <button
                        onClick={async () => {
                          if (!teacherReplyMessage.trim()) {
                            toast.error('Please enter a message');
                            return;
                          }

                          setIsSubmittingReply(true);
                          try {
                            const requestRef = doc(db, 'teacherRequests', selectedRequest.id);
                            const discussions = selectedRequest.discussions || [];
                            
                            const newDiscussion = {
                              message: teacherReplyMessage.trim(),
                              authorName: user?.displayName || user?.email || 'Teacher',
                              authorId: user?.uid || 'teacher',
                              createdAt: new Date() // Use regular Date instead of serverTimestamp() for arrays
                            };

                            discussions.push(newDiscussion);

                            await updateDoc(requestRef, {
                              discussions: discussions,
                              updatedAt: serverTimestamp()
                            });

                            toast.success('Reply sent successfully!');
                            setTeacherReplyMessage('');
                            
                            // Update selected request to show new discussion
                            setSelectedRequest({
                              ...selectedRequest,
                              discussions: discussions
                            });
                            
                            // Reload requests to update the list
                            loadTeacherRequests();
                          } catch (error) {
                            toast.error('Failed to send reply. Please try again.');
                          } finally {
                            setIsSubmittingReply(false);
                          }
                        }}
                        disabled={isSubmittingReply || !teacherReplyMessage.trim()}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center space-x-2"
                      >
                        {isSubmittingReply ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            <span>Send Reply</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ batch, content, members }) => {
  const totalContent = content.posts.length + content.assignments.length + content.discussions.length;
  
  return (
    <div className="h-full flex flex-col space-y-3 sm:space-y-4 lg:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 sm:p-3 lg:p-4">
          <div className="flex items-center">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-blue-600 dark:text-blue-400" />
            <div className="ml-2 sm:ml-3">
              <p className="text-xs sm:text-sm font-medium text-blue-800 dark:text-blue-300">Notes</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900 dark:text-blue-200">{content.posts.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2 sm:p-3 lg:p-4">
          <div className="flex items-center">
            <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-green-600 dark:text-green-400" />
            <div className="ml-2 sm:ml-3">
              <p className="text-xs sm:text-sm font-medium text-green-800 dark:text-green-300">Assignments</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-900 dark:text-green-200">{content.assignments.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 sm:p-3 lg:p-4">
          <div className="flex items-center">
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-purple-600 dark:text-purple-400" />
            <div className="ml-2 sm:ml-3">
              <p className="text-xs sm:text-sm font-medium text-purple-800 dark:text-purple-300">Discussions</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-900 dark:text-purple-200">{content.discussions.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2 sm:p-3 lg:p-4">
          <div className="flex items-center">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-orange-600 dark:text-orange-400" />
            <div className="ml-2 sm:ml-3">
              <p className="text-xs sm:text-sm font-medium text-orange-800 dark:text-orange-300">Students</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-900 dark:text-orange-200">{members.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 flex-1 min-h-0">
        {/* Recent Activity */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {totalContent === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-4">
                No activity yet. Create your first content to get started!
              </p>
            ) : (
              <>
                {content.posts.slice(0, 3).map((post) => (
                  <div key={post.id} className="flex items-center space-x-3">
                    <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{post.title}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Note • {new Date(post.createdAt?.seconds * 1000).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
                {content.assignments.slice(0, 2).map((assignment) => (
                  <div key={assignment.id} className="flex items-center space-x-3">
                    <BookOpen className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{assignment.title}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Assignment • {new Date(assignment.createdAt?.seconds * 1000).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Batch Info */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Batch Information</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Created</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {new Date(batch.createdAt?.seconds * 1000).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                <CheckCircle className="w-3 h-3 mr-1" />
                Active
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Content</p>
              <p className="font-medium text-gray-900 dark:text-white">{totalContent} items</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Posts Tab Component
const PostsTab = ({ posts, onCreatePost, onDeletePost, onDeleteFile }) => {
  const renderFilePreview = (file, postId, fileIndex) => {
    const isImage = file.type?.startsWith('image/');
    const isPDF = file.type === 'application/pdf' || file.name?.endsWith('.pdf');
    
    return (
      <div key={fileIndex} className="relative group border border-gray-200 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-800">
        <div className="flex items-center space-x-2">
          {isImage ? (
            <img 
              src={file.url} 
              alt={file.name} 
              className="w-16 h-16 object-cover rounded"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <div className="flex items-center space-x-1">
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400"
              title="View/Download"
            >
              <Eye className="w-4 h-4" />
            </a>
            <button
              onClick={() => onDeleteFile(postId, fileIndex, 'post')}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400"
              title="Delete file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col space-y-2 sm:space-y-3 lg:space-y-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Notes & Posts</h3>
        <button
          onClick={onCreatePost}
          className="flex items-center px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm"
        >
          <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Create Note</span>
          <span className="sm:hidden">Create</span>
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-8 sm:py-12 flex-1 flex items-center justify-center">
          <div>
            <FileText size={40} className="sm:w-12 sm:h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3 sm:mb-4" />
            <h4 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-1 sm:mb-2">No notes yet</h4>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
              Create your first note to share information with your students.
            </p>
            <button
              onClick={onCreatePost}
              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm"
            >
              Create Your First Note
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 lg:gap-4 flex-1 overflow-y-auto min-h-0">
          {posts.map((post) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold text-gray-900 dark:text-white">{post.title}</h4>
                <button
                  onClick={() => onDeletePost(post.id)}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                {post.content}
              </p>
              
              {/* File Attachments */}
              {post.attachments && post.attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Attachments:</p>
                  <div className="space-y-2">
                    {post.attachments.map((file, index) => renderFilePreview(file, post.id, index))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-3">
                <span>By {post.authorName}</span>
                <span>{new Date(post.createdAt?.seconds * 1000).toLocaleDateString()}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Assignments Tab Component
const AssignmentsTab = ({ assignments, assignmentStats, onCreateAssignment, onDeleteAssignment, onDeleteFile, batchId }) => {
  const [expandedAssignment, setExpandedAssignment] = useState(null);

  const renderFilePreview = (file, assignmentId, fileIndex) => {
    const isImage = file.type?.startsWith('image/');
    const isPDF = file.type === 'application/pdf' || file.name?.endsWith('.pdf');
    
    return (
      <div key={fileIndex} className="relative group border border-gray-200 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-800">
        <div className="flex items-center space-x-2">
          {isImage ? (
            <img 
              src={file.url} 
              alt={file.name} 
              className="w-16 h-16 object-cover rounded"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <div className="flex items-center space-x-1">
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400"
              title="View/Download"
            >
              <Eye className="w-4 h-4" />
            </a>
            <button
              onClick={() => onDeleteFile(assignmentId, fileIndex, 'assignment')}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400"
              title="Delete file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col space-y-2 sm:space-y-3 lg:space-y-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Assignments</h3>
        <button
          onClick={onCreateAssignment}
          className="flex items-center px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs sm:text-sm"
        >
          <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Create Assignment</span>
          <span className="sm:hidden">Create</span>
        </button>
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-8 sm:py-12 flex-1 flex items-center justify-center">
          <div>
            <BookOpen size={40} className="sm:w-12 sm:h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3 sm:mb-4" />
            <h4 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-1 sm:mb-2">No assignments yet</h4>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
              Create your first assignment to give students practice and assessment.
            </p>
            <button
              onClick={onCreateAssignment}
              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs sm:text-sm"
            >
              Create Your First Assignment
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-3 lg:space-y-4 flex-1 overflow-y-auto min-h-0">
          {assignments.map((assignment) => {
            const stats = assignmentStats[assignment.id] || { total: 0, submitted: 0, remaining: 0, submittedStudents: [], remainingStudents: [] };
            const isExpanded = expandedAssignment === assignment.id;
            
            return (
              <motion.div
                key={assignment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{assignment.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        stats.submitted === stats.total && stats.total > 0
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                      }`}>
                        {stats.submitted}/{stats.total} Submitted
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{assignment.content || assignment.description}</p>
                    
                    {/* File Attachments */}
                    {assignment.attachments && assignment.attachments.length > 0 && (
                      <div className="mt-3 mb-3">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Attachments:</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {assignment.attachments.map((file, index) => renderFilePreview(file, assignment.id, index))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        <span>Due: {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        <span>Created: {new Date(assignment.createdAt?.seconds * 1000).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onDeleteAssignment(assignment.id)}
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 ml-4"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Submission Tracking */}
                {stats.total > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => setExpandedAssignment(isExpanded ? null : assignment.id)}
                      className="w-full flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    >
                      <span>View Submissions ({stats.submitted} submitted, {stats.remaining} remaining)</span>
                      <span className="transform transition-transform">{isExpanded ? '▼' : '▶'}</span>
                    </button>
                    
                    {isExpanded && (
                      <div className="mt-4 space-y-4">
                        {/* Submitted Students */}
                        {stats.submittedStudents.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2 flex items-center">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Submitted ({stats.submittedStudents.length})
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {stats.submittedStudents.map((student) => {
                                const submission = stats.submissions?.find(s => 
                                  s.studentId === (student.studentId || student.userId)
                                );
                                return (
                                  <div key={student.id || student.studentId} className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                          {student.studentName || 'Student'}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                          {student.studentEmail}
                                        </p>
                                        {submission && (
                                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                            Submitted: {new Date(submission.submittedAt?.seconds * 1000).toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Remaining Students */}
                        {stats.remainingStudents.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-2 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              Pending ({stats.remainingStudents.length})
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {stats.remainingStudents.map((student) => (
                                <div key={student.id || student.studentId} className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {student.studentName || 'Student'}
                                      </p>
                                      <p className="text-xs text-gray-600 dark:text-gray-400">
                                        {student.studentEmail}
                                      </p>
                                    </div>
                                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Discussions Tab Component
const DiscussionsTab = ({ discussions, onCreateDiscussion, onDeleteDiscussion }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Discussions</h3>
        <button
          onClick={onCreateDiscussion}
          className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Start Discussion
        </button>
      </div>

      {discussions.length === 0 ? (
        <div className="text-center py-12">
          <MessageCircle size={48} className="text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No discussions yet</h4>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Start a discussion to encourage student interaction and learning.
          </p>
          <button
            onClick={onCreateDiscussion}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Start Your First Discussion
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {discussions.map((discussion) => (
            <motion.div
              key={discussion.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{discussion.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{discussion.description}</p>
                  
                  <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                    <span>By {discussion.authorName}</span>
                    <span>{new Date(discussion.createdAt?.seconds * 1000).toLocaleDateString()}</span>
                    <span>{discussion.replies?.length || 0} replies</span>
                  </div>
                </div>
                <button
                  onClick={() => onDeleteDiscussion(discussion.id)}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 ml-4"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Members Tab Component
const MembersTab = ({ members, batchId, onRemoveMember }) => {
  const { currentUser: user } = useRealtime();
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [hiredStudents, setHiredStudents] = useState([]);
  const [loadingHiredStudents, setLoadingHiredStudents] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Load hired students when modal opens
  const loadHiredStudents = async () => {
    if (!user) return;
    
    try {
      setLoadingHiredStudents(true);
      const students = await teacherHiringService.getTeacherHiredStudents(user.uid);
      setHiredStudents(students);
    } catch (error) {
      toast.error('Failed to load hired students');
    } finally {
      setLoadingHiredStudents(false);
    }
  };

  const handleAddStudent = async (studentId, studentName) => {
    setIsAdding(true);
    try {
      // Add student to batch
      await teacherHiringService.addBatchMember(batchId, studentId, 'student');
      toast.success(`${studentName} added to batch successfully!`);
      setShowAddStudent(false);
      // Refresh the page or call a callback to reload members
      window.location.reload(); // Simple refresh for now
    } catch (error) {
      toast.error('Failed to add student to batch');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (window.confirm(`Are you sure you want to remove ${memberName} from this batch?`)) {
      try {
        await onRemoveMember(memberId);
        toast.success('Student removed successfully!');
      } catch (error) {
        toast.error('Failed to remove student');
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Batch Members ({members.length})
        </h3>
        <button 
          onClick={() => {
            setShowAddStudent(true);
            loadHiredStudents();
          }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Student
        </button>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-12">
          <Users size={48} className="text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No students yet</h4>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Add students to this batch to start sharing content with them.
          </p>
          <button 
            onClick={() => {
              setShowAddStudent(true);
              loadHiredStudents();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Your First Student
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">
                    {member.studentName?.charAt(0) || 'S'}
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {member.studentName || 'Student'}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {member.studentEmail}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveMember(member.id, member.studentName || 'Student')}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  title="Remove student"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Joined: {new Date(member.joinedAt?.seconds * 1000).toLocaleDateString()}</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                  Active
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add Student Modal */}
      {showAddStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Select Student to Add
                </h2>
                <button
                  onClick={() => setShowAddStudent(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Choose from students who have hired you:
                </p>
              </div>

              {loadingHiredStudents ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading students...</p>
                </div>
              ) : hiredStudents.length === 0 ? (
                <div className="text-center py-8">
                  <Users size={48} className="text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No hired students</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    You don't have any students who have hired you yet.
                  </p>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-3">
                  {hiredStudents.map((student) => {
                    // Check if student is already in this batch
                    const isAlreadyInBatch = members.some(member => member.studentId === student.studentId);
                    
                    return (
                      <div
                        key={student.id}
                        className={`p-4 border rounded-lg ${
                          isAlreadyInBatch 
                            ? 'border-gray-200 bg-gray-50 dark:bg-gray-700 dark:border-gray-600' 
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 dark:border-gray-600 dark:hover:border-blue-500 dark:hover:bg-blue-900/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 dark:text-blue-400 font-semibold">
                                {student.studentName?.charAt(0) || 'S'}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {student.studentName || 'Student'}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {student.studentEmail}
                              </p>
                              {student.subject && (
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  Subject: {student.subject}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {isAlreadyInBatch ? (
                              <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 text-sm rounded-full">
                                Already in batch
                              </span>
                            ) : (
                              <button
                                onClick={() => handleAddStudent(student.studentId, student.studentName)}
                                disabled={isAdding}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                              >
                                {isAdding ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Adding...</span>
                                  </>
                                ) : (
                                  <>
                                    <UserPlus className="w-4 h-4" />
                                    <span>Add</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-end pt-4 mt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={() => setShowAddStudent(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Create Content Modal Component
const CreateContentModal = ({ type, batchId, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    description: '',
    dueDate: '',
    attachments: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileProgress, setFileProgress] = useState({});

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (!batchId) {
      toast.error('Batch ID is missing. Please try again.');
      return;
    }

    // Validate file types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      toast.error('Only images (JPEG, PNG, GIF, WebP) and PDF files are allowed');
      return;
    }

    // Validate file sizes (max 10MB per file)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFiles(true);
    const uploadedFiles = [];

    try {
      for (const file of files) {
        try {
          const fileData = await batchContentService.uploadBatchFile(batchId, file, type);
          uploadedFiles.push(fileData);
          setFileProgress(prev => ({ ...prev, [file.name]: 100 }));
        } catch (error) {
          toast.error(`Failed to upload ${file.name}: ${error.message}`);
        }
      }

      if (uploadedFiles.length > 0) {
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, ...uploadedFiles]
        }));
        toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
      }
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploadingFiles(false);
      setFileProgress({});
      // Reset file input
      e.target.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setFormData({ title: '', content: '', description: '', dueDate: '', attachments: [] });
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModalTitle = () => {
    switch (type) {
      case 'post': return 'Create Note';
      case 'assignment': return 'Create Assignment';
      case 'discussion': return 'Start Discussion';
      default: return 'Create Content';
    }
  };

  const getContentLabel = () => {
    switch (type) {
      case 'post': return 'Note Content';
      case 'assignment': return 'Assignment Description';
      case 'discussion': return 'Discussion Description';
      default: return 'Content';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {getModalTitle()}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder={`Enter ${type} title`}
                required
              />
            </div>

            {type === 'assignment' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {getContentLabel()} *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder={`Enter ${type} content`}
                rows={6}
                required
              />
            </div>

            {/* File Upload Section */}
            {(type === 'post' || type === 'assignment') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attachments (Images & PDFs)
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleFileSelect}
                    disabled={uploadingFiles}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className={`flex flex-col items-center justify-center cursor-pointer ${
                      uploadingFiles ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Paperclip className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {uploadingFiles ? 'Uploading...' : 'Click to upload images or PDFs'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Max 10MB per file
                    </span>
                  </label>
                </div>

                {/* Uploaded Files Preview */}
                {formData.attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Uploaded Files:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {formData.attachments.map((file, index) => {
                        const isImage = file.type?.startsWith('image/');
                        return (
                          <div key={index} className="relative group border border-gray-200 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-800">
                            {isImage ? (
                              <img 
                                src={file.url} 
                                alt={file.name} 
                                className="w-full h-20 object-cover rounded mb-1"
                              />
                            ) : (
                              <div className="w-full h-20 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center mb-1">
                                <FileText className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                            <button
                              onClick={() => handleRemoveFile(index)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove file"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.title.trim() || !formData.content.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Create {type.charAt(0).toUpperCase() + type.slice(1)}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default TeacherBatchManagement;
